import React from 'react';

/**
 * The three view-tab definitions shared by `pages/MyHires.js`,
 * `pages/AdminHires.js` and `pages/DepartmentHires.js` — extracted
 * 2026-09-18 when all three pages' hand-rolled Kanban/Gantt/Calendar tab
 * block (identical classes, identical `sfx_hires_view` storage key,
 * identical three views — `AdminHires.js`'s own docblock had flagged this
 * exact triplication as owed work) migrated onto the shared `ui/Tabs`.
 *
 * Same reasoning `hooks/useHireSearch.js` already gives for the search-box
 * derivation these three pages share: a third (by now fourth) inline copy
 * of the same array is the thing the house convention forbids, not a
 * stylistic preference.
 *
 * `ui/Tabs` takes `tabs` already built and gated — it renders a `label`
 * verbatim, so the icon lives INSIDE the label node here (a `<span>` with
 * its own `gap-1.5`) rather than as a separate prop `ui/Tabs` has no slot
 * for; that reproduces the icon-then-text layout the hand-rolled buttons
 * had via the button's own `gap-1.5`.
 */
export const HIRES_VIEW_IDS = ['kanban', 'gantt', 'calendar'];

/**
 * 🔀 THE TABLE VIEW (2026-09-27) — Department Hires and My Hires only
 * (`withTable`); `AdminHires.js` passes nothing and is unchanged. It is FIRST
 * in the strip because it is the desktop DEFAULT.
 *
 * `sfx_hires_view` is ONE key shared by all three pages, so a stored
 * `'table'` reaches `AdminHires.js` too — which validates against
 * `HIRES_VIEW_IDS` (no table) and lands on Kanban. Correct, not a leak.
 */
export const HIRES_VIEW_KEY = 'sfx_hires_view';
export const HIRES_VIEW_IDS_WITH_TABLE = ['table', ...HIRES_VIEW_IDS];

/**
 * The view to open on, for a page that offers the table. Mirrors
 * the order pages' old viewport default (owner ruling
 * 2026-09-27: *"Table is the default view on desktop (≥768px), cards/Kanban
 * on phone"*): an explicit SAVED choice always wins, on either size;
 * otherwise TABLE on desktop and KANBAN below 768px. No `matchMedia` (jsdom)
 * or a throw → desktop. Resolved ONCE at mount, like the order pages — a
 * resize mid-session does not switch view. Storage throwing (private mode) →
 * the viewport default, never a crash.
 * 🪤 `sfx_hires_view` is written on every tab CLICK (never on hydration), so
 * anyone who ever clicked a view keeps it — "saved choice wins" by ruling.
 */
export function readInitialHiresView() {
  let saved = null;
  try { saved = window.localStorage.getItem(HIRES_VIEW_KEY); } catch (e) { saved = null; }
  if (HIRES_VIEW_IDS_WITH_TABLE.includes(saved)) return saved;
  try {
    if (window.matchMedia('(max-width: 767px)').matches) return 'kanban';
  } catch (e) { /* no matchMedia — desktop default */ }
  return 'table';
}

const HIRES_TAB_ICON = {
  table: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18M10 4v16M4 4h16a1 1 0 011 1v14a1 1 0 01-1 1H4a1 1 0 01-1-1V5a1 1 0 011-1z" />
    </svg>
  ),
  kanban: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" />
    </svg>
  ),
  gantt: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h10M4 18h14" />
    </svg>
  ),
  calendar: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
};

export const HIRES_TAB_LABEL_TEXT = { table: 'Table', kanban: 'Kanban', gantt: 'Gantt', calendar: 'Calendar' };

/**
 * Build the `tabs` prop for `ui/Tabs`.
 *
 * `hideGantt` DROPS the Gantt tab entirely rather than styling it hidden —
 * `MyHires.js` / `DepartmentHires.js` pass their own `isMobile` (below the
 * `md` breakpoint, where the horizontal Gantt timeline does not fit; same
 * threshold and reasoning as before this migration). `AdminHires.js` always
 * passes `false` — that page has no mobile fallback and never hid Gantt.
 */
export function buildHiresViewTabs(hideGantt, withTable = false) {
  // The table is offered on a phone too (never dropped like Gantt): it is not
  // the default there, but an explicit choice must be reachable and usable.
  return (withTable ? HIRES_VIEW_IDS_WITH_TABLE : HIRES_VIEW_IDS)
    .filter((id) => !(hideGantt && id === 'gantt'))
    .map((id) => ({
      id,
      label: (
        <span className="inline-flex items-center gap-1.5">
          {HIRES_TAB_ICON[id]}
          {HIRES_TAB_LABEL_TEXT[id]}
        </span>
      ),
    }));
}
